import React, { useRef, useState, useEffect, useCallback } from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Snackbar from "@mui/material/Snackbar";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import OpenInBrowserIcon from "@mui/icons-material/OpenInBrowser";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { isMobileBrowser } from "../utils/detect_mobile_browser";
import { isNativeApp } from "../utils/mobiloud_detection";
import { capacitorOpenExternal } from "../capacitor/browser";
import { copyToClipboard } from "./clipboard";

const DEFAULT_MENU_POSITION = { top: 0, left: 0 };

function findAnchorAncestor(element, root) {
  let el = element;
  while (el && el !== root) {
    if (el.tagName === "A" && el.hasAttribute("href")) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

function isExternalLink(href) {
  try {
    const url = new URL(href, window.location.origin);
    if (url.protocol === "mailto:" || url.protocol === "tel:") return false;
    if (url.protocol === "http:" || url.protocol === "https:") {
      return url.origin !== window.location.origin;
    }
    return false;
  } catch {
    return false;
  }
}

function shouldIntercept(anchor) {
  const href = anchor.getAttribute("href");
  if (!href) return false;
  return isExternalLink(href);
}

export default function MobileLinkContextMenuProvider({ children }) {
  const containerRef = useRef(null);
  const [menuState, setMenuState] = useState({
    open: false,
    url: null,
    position: DEFAULT_MENU_POSITION,
  });
  const [copyErrorOpen, setCopyErrorOpen] = useState(false);

  const handleContextMenu = useCallback((event) => {
    if (!isMobileBrowser) return;

    const anchor = findAnchorAncestor(event.target, containerRef.current);
    if (!anchor) return;
    if (!shouldIntercept(anchor)) return;

    event.preventDefault();
    event.stopPropagation();

    const url = anchor.href;
    const touch = event.touches?.[0];
    const x = touch ? touch.clientX : event.clientX;
    const y = touch ? touch.clientY : event.clientY;

    setMenuState({ open: true, url, position: { top: y, left: x } });
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener("contextmenu", handleContextMenu, true);
    return () => {
      container.removeEventListener("contextmenu", handleContextMenu, true);
    };
  }, [handleContextMenu]);

  const handleClose = () => {
    setMenuState({ open: false, url: null, position: DEFAULT_MENU_POSITION });
  };

  const handleOpenLink = () => {
    window.location.href = menuState.url;
    handleClose();
  };

  const handleOpenInBrowser = async () => {
    const url = menuState.url;
    handleClose();
    if (!url) {
      return;
    }
    // In the Capacitor shell, open in the system browser; fall back to a new tab
    // (MobiLoud path and plain web).
    if (await capacitorOpenExternal(url)) {
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleCopyUrl = async () => {
    if (!menuState.url) {
      handleClose();
      return;
    }

    try {
      await copyToClipboard(menuState.url);
    } catch {
      setCopyErrorOpen(true);
    } finally {
      handleClose();
    }
  };

  const showOpenInBrowser = isNativeApp();

  return (
    <div ref={containerRef} style={{ display: "contents" }}>
      {children}
      <Menu
        open={menuState.open}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={menuState.position}
      >
        <MenuItem onClick={handleOpenLink}>
          <ListItemIcon>
            <OpenInNewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open Link</ListItemText>
        </MenuItem>
        {showOpenInBrowser && (
          <MenuItem onClick={handleOpenInBrowser}>
            <ListItemIcon>
              <OpenInBrowserIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Open in Browser</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleCopyUrl}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy URL</ListItemText>
        </MenuItem>
      </Menu>
      <Snackbar
        open={copyErrorOpen}
        autoHideDuration={4000}
        onClose={() => setCopyErrorOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message="Unable to copy link. Please copy it manually."
      />
    </div>
  );
}
